import React, { useEffect, useRef, useState } from 'react';
import { Redirect, useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { ApplicationState } from 'store';
import { ProfileState } from 'store/modules/profile_store/types';
import { CancelTokenSource } from 'axios';
import PriceIntelligenceSearch from 'components/organisms/price_intelligence_search';
import PriceIntelligenceResults, {
  QuantidadeProjeto,
} from 'components/organisms/price_intelligence_results';
import PriceIntelligenceBatch, {
  AndamentoLote,
  AvisoLote,
} from 'components/organisms/price_intelligence_batch';
import PriceIntelligenceProgress from 'components/molecules/price_intelligence_progress';
import PriceIntelligenceMarket from 'components/organisms/price_intelligence_market';
import PriceIntelligenceClarification from 'components/molecules/price_intelligence_clarification';
import PriceIntelligenceSaveModal from 'components/molecules/price_intelligence_save_modal';
import PriceIntelligenceImportModal from 'components/molecules/price_intelligence_import_modal';
import createTrack from 'services/actions/tracker_actions';
import { canAccessPriceIntelligence } from 'services/alfa_tools';
import { bloqueioDaAssinatura } from 'services/assinatura';
import BlockedSubscription from 'components/molecules/blocked_subscription';
import BlockedSubscriptionRepresentant from 'components/molecules/blocked_subscription_representant';
import {
  AnalisarResponse,
  criarCancelamento,
  extrairQuantidadeDoTexto,
  FILTROS_AMOSTRAS_VAZIOS,
  FiltrosAmostras,
  FiltrosPesquisa,
  foiCancelada,
  parametrosDosFiltros,
  PERIODO_PADRAO,
  PERIODO_PERSONALIZADO,
  priceIntelligenceApi,
  regiaoUnicaDe,
  SavedSearchSnapshot,
  SearchResponse,
  snapshotDoResultado,
  textoParaAnalise,
  TipoQuery,
  UFS,
  unidadeCanonica,
} from 'services/price_intelligence';
import {
  carregarLote,
  chaveDoLote,
  exportarLoteCsv,
  formatarQuantidade,
  guardarResultado,
  ItemLote,
  lerResultado,
  Lote,
  manterSoResultadosDe,
  marcarPesquisado,
  marcarSalvo,
  montarLote,
  nomeDaPesquisaDoItem,
  nomeDoLote,
  RascunhoItem,
  removerItem,
  ResultadoGuardado,
  resumoDoResultado,
  salvarLote,
  textoDeBusca,
} from 'services/price_intelligence_batch';
import AmbassadorToolFeedback from 'components/molecules/ambassador_tool_feedback';
import { PRICE_INTELLIGENCE_AMBASSADOR_SLUG } from 'services/ambassador_tools';
import { Container } from './styles';

type Fase = 'idle' | 'analisando' | 'clarificando' | 'buscando' | 'concluindo';

interface ContextoBusca {
  query: string;
  queries: string[] | null;
  specAssumida: string | null;
  tipoQuery: TipoQuery | null;
}

const PERIODOS_VALIDOS = [3, 6, 12, 18, 24];

const lerStorage = (chave: string): string | null => {
  try {
    return window.localStorage.getItem(chave);
  } catch (e) {
    return null;
  }
};

const gravarStorage = (chave: string, valor: string) => {
  try {
    window.localStorage.setItem(chave, valor);
  } catch (e) {
    // sem persistência de filtros
  }
};

const ufsValidas = (lista: string[]) =>
  lista.map((uf) => uf.trim().toUpperCase()).filter((uf) => UFS.indexOf(uf) >= 0);

const periodoSalvo = (): number => {
  const salvo = Number(lerStorage('pi_periodo'));
  return PERIODOS_VALIDOS.indexOf(salvo) >= 0 ? salvo : PERIODO_PADRAO;
};

const ufsSalvas = (): string[] => {
  try {
    const lista = JSON.parse(lerStorage('pi_ufs') || '[]');
    return Array.isArray(lista) ? ufsValidas(lista) : [];
  } catch (e) {
    return [];
  }
};

const chaveDosFiltros = (f: FiltrosPesquisa) =>
  `${f.periodoMeses}|${f.dataDe}|${f.dataAte}|${f.ufs.join(',')}`;

const PriceIntelligence = () => {
  const location = useLocation();
  const history = useHistory();
  const { profile } = useSelector<ApplicationState, ProfileState>((s) => s.storage.profile);
  const userId = profile ? profile.id : null;

  const [query, setQuery] = useState<string>('');
  const [queryPesquisada, setQueryPesquisada] = useState<string>('');
  const [periodoMeses, setPeriodoMeses] = useState<number>(periodoSalvo);
  const [dataDe, setDataDe] = useState<string>('');
  const [dataAte, setDataAte] = useState<string>('');
  const [ufs, setUfs] = useState<string[]>(ufsSalvas);

  const [fase, setFase] = useState<Fase>('idle');
  const [analise, setAnalise] = useState<AnalisarResponse | null>(null);
  const [detalhes, setDetalhes] = useState<string>('');
  const [resultado, setResultado] = useState<SearchResponse | null>(null);
  const [versaoResultado, setVersaoResultado] = useState<number>(0);
  const [projetoQtd, setProjetoQtd] = useState<QuantidadeProjeto | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [totalItens, setTotalItens] = useState<number | null>(null);
  const [termoPanorama, setTermoPanorama] = useState<string>('');
  const [filtrosPanorama, setFiltrosPanorama] = useState<FiltrosAmostras>(FILTROS_AMOSTRAS_VAZIOS);

  const [snapshotSalvar, setSnapshotSalvar] = useState<SavedSearchSnapshot | null>(null);
  const [salvarItemId, setSalvarItemId] = useState<string | null>(null);
  const [salvando, setSalvando] = useState<boolean>(false);
  const [erroSalvar, setErroSalvar] = useState<string | null>(null);
  const [salvaComSucesso, setSalvaComSucesso] = useState<boolean>(false);

  const [lote, setLote] = useState<Lote | null>(() => (userId ? carregarLote(userId) : null));
  const [itemAtivoId, setItemAtivoId] = useState<string | null>(null);
  const [importarAberto, setImportarAberto] = useState<boolean>(false);
  const [andamento, setAndamento] = useState<AndamentoLote | null>(null);
  const [falhas, setFalhas] = useState<Record<string, boolean>>({});
  const [salvandoTodas, setSalvandoTodas] = useState<{ feitos: number; total: number } | null>(
    null,
  );
  const [aviso, setAviso] = useState<AvisoLote | null>(null);

  const cancelRef = useRef<CancelTokenSource | null>(null);
  const geracaoRef = useRef<number>(0);
  const concluirTimerRef = useRef<number | null>(null);
  const loteRef = useRef<Lote | null>(lote);
  const itemAtivoRef = useRef<string | null>(null);
  const filaRef = useRef<{ cancelado: boolean; cancel: CancelTokenSource | null } | null>(null);
  const resultadosRef = useRef<Record<string, ResultadoGuardado>>({});
  const ultimaBuscaRef = useRef<ContextoBusca | null>(null);
  const filtrosDaBuscaRef = useRef<FiltrosPesquisa | null>(null);
  const analiseRef = useRef<AnalisarResponse | null>(null);
  const resultadoRef = useRef<SearchResponse | null>(null);
  const salvarTodasCanceladoRef = useRef<boolean>(false);
  const faseRef = useRef<Fase>('idle');

  itemAtivoRef.current = itemAtivoId;
  faseRef.current = fase;
  analiseRef.current = analise;
  resultadoRef.current = resultado;

  const filtrosAtuais = (): FiltrosPesquisa => ({ periodoMeses, dataDe, dataAte, ufs });
  const filtrosAplicadosRef = useRef<string>(chaveDosFiltros(filtrosAtuais()));

  const loteRodando = Boolean(andamento);
  const itemAtivo =
    itemAtivoId && lote ? lote.itens.find((i) => i.id === itemAtivoId) || null : null;

  const mudarLote = (mudar: (atual: Lote | null) => Lote | null) => {
    const novo = mudar(loteRef.current);
    loteRef.current = novo;
    setLote(novo);
    if (userId) salvarLote(userId, novo);
  };

  const aplicarFiltros = (f: FiltrosPesquisa) => {
    filtrosAplicadosRef.current = chaveDosFiltros(f);
    setPeriodoMeses(f.periodoMeses);
    setDataDe(f.dataDe);
    setDataAte(f.dataAte);
    setUfs(f.ufs);
  };

  const abrirGeracao = () => {
    geracaoRef.current += 1;
    if (cancelRef.current) cancelRef.current.cancel();
    cancelRef.current = criarCancelamento();
    if (concluirTimerRef.current) window.clearTimeout(concluirTimerRef.current);
    return { geracao: geracaoRef.current, cancel: cancelRef.current };
  };

  const extrairErro = (err: any): string => {
    if (err && err.response && err.response.data && err.response.data.error) {
      return err.response.data.error;
    }
    return 'Não foi possível concluir a pesquisa agora. Tente novamente em instantes.';
  };

  const registrarResultado = (
    itemId: string,
    guardado: ResultadoGuardado,
    refinar: string | null,
  ) => {
    resultadosRef.current[itemId] = guardado;
    if (userId) guardarResultado(userId, itemId, guardado);
    mudarLote((atual) =>
      atual
        ? marcarPesquisado(atual, itemId, { ...resumoDoResultado(guardado.resultado), refinar })
        : atual,
    );
    setFalhas((atual) => {
      if (!atual[itemId]) return atual;
      const resto = { ...atual };
      delete resto[itemId];
      return resto;
    });
  };

  const obterResultado = async (itemId: string): Promise<ResultadoGuardado | null> => {
    if (resultadosRef.current[itemId]) return resultadosRef.current[itemId];
    if (!userId) return null;
    const lido = await lerResultado(userId, itemId);
    if (lido) resultadosRef.current[itemId] = lido;
    return lido;
  };

  const buscarPesado = async (contexto: ContextoBusca, filtros: FiltrosPesquisa) => {
    if (filaRef.current) return;
    const { geracao, cancel } = abrirGeracao();
    const itemId = itemAtivoRef.current;
    ultimaBuscaRef.current = contexto;
    setFase('buscando');
    setErro(null);

    try {
      const data = await priceIntelligenceApi.search(
        { ...contexto, ...parametrosDosFiltros(filtros) },
        cancel,
      );
      if (geracao !== geracaoRef.current) return;

      setFase('concluindo');
      concluirTimerRef.current = window.setTimeout(() => {
        if (geracao !== geracaoRef.current) return;
        filtrosDaBuscaRef.current = filtros;
        setResultado(data);
        setVersaoResultado((v) => v + 1);
        setTermoPanorama(contexto.query);
        setFiltrosPanorama(FILTROS_AMOSTRAS_VAZIOS);
        setFase('idle');
        if (itemId && itemId === itemAtivoRef.current) {
          registrarResultado(
            itemId,
            {
              resultado: data,
              busca: contexto,
              filtros,
              analise: analiseRef.current,
              quando: new Date().toISOString(),
            },
            null,
          );
        }
      }, 900);
    } catch (err) {
      if (foiCancelada(err) || geracao !== geracaoRef.current) return;
      const mensagem = extrairErro(err);
      createTrack('Análise de Preços - Pesquisa falhou', {
        query: contexto.query,
        motivo: mensagem,
      });
      setErro(mensagem);
      setFase('idle');
    }
  };

  const contextoDaAnalise = (texto: string, a: AnalisarResponse | null): ContextoBusca => ({
    query: texto,
    queries: a ? a.queries : null,
    specAssumida: a ? a.spec_assumida : null,
    tipoQuery: a ? a.tipo : null,
  });

  const iniciarBusca = async (texto?: string, filtros: FiltrosPesquisa = filtrosAtuais()) => {
    const textoBusca = (texto !== undefined ? texto : query).trim();
    if (!textoBusca || filaRef.current) return;
    if (texto !== undefined) setQuery(texto);

    const { geracao, cancel } = abrirGeracao();
    setQueryPesquisada(textoBusca);
    setResultado(null);
    setAnalise(null);
    setDetalhes('');
    setProjetoQtd(null);
    setErro(null);
    setSalvaComSucesso(false);
    setFase('analisando');
    createTrack('Análise de Preços - Pesquisou', {
      query: textoBusca,
      item_do_lote: Boolean(itemAtivoRef.current),
    });

    let preAnalise: AnalisarResponse | null = null;
    try {
      preAnalise = await priceIntelligenceApi.analyze(textoParaAnalise(textoBusca), cancel);
    } catch (err) {
      if (foiCancelada(err) || geracao !== geracaoRef.current) return;
      preAnalise = null;
    }
    if (geracao !== geracaoRef.current) return;

    setAnalise(preAnalise);
    if (preAnalise && preAnalise.clarificacao && preAnalise.clarificacao.necessaria) {
      setFase('clarificando');
      return;
    }

    const unidadeTipica = preAnalise ? preAnalise.unidade_fisica_tipica : null;
    const qtd = unidadeTipica ? extrairQuantidadeDoTexto(textoBusca, unidadeTipica) : null;
    setProjetoQtd(qtd ? { valor: qtd, unidade: unidadeCanonica(unidadeTipica) } : null);
    await buscarPesado(contextoDaAnalise(textoBusca, preAnalise), filtros);
  };

  const confirmarClarificacao = async () => {
    if (!analise || filaRef.current) return;
    const extra = detalhes.trim();
    setDetalhes('');
    setResultado(null);
    createTrack('Análise de Preços - Respondeu clarificação', { com_detalhes: Boolean(extra) });

    const perguntaQtd = analise.clarificacao.perguntas.find((p) => p.tipo === 'quantidade');
    const unidadeAlvo =
      (perguntaQtd && perguntaQtd.unidade) || analise.unidade_fisica_tipica || null;
    const qtd = unidadeAlvo
      ? extrairQuantidadeDoTexto(extra, unidadeAlvo) ||
        extrairQuantidadeDoTexto(queryPesquisada, unidadeAlvo)
      : null;
    setProjetoQtd(qtd ? { valor: qtd, unidade: unidadeCanonica(unidadeAlvo) } : null);

    const filtros = filtrosAtuais();
    if (!extra) {
      await buscarPesado(contextoDaAnalise(queryPesquisada, analise), filtros);
      return;
    }

    const textoBusca = `${queryPesquisada} — ${extra}`;
    setQueryPesquisada(textoBusca);
    const { geracao, cancel } = abrirGeracao();
    setFase('analisando');
    let refinada: AnalisarResponse | null = analise;
    try {
      refinada = await priceIntelligenceApi.analyze(
        textoParaAnalise(queryPesquisada, extra),
        cancel,
      );
    } catch (err) {
      if (foiCancelada(err) || geracao !== geracaoRef.current) return;
      refinada = null;
    }
    if (geracao !== geracaoRef.current) return;
    if (refinada) setAnalise(refinada);
    await buscarPesado(contextoDaAnalise(textoBusca, refinada), filtros);
  };

  const cancelarBusca = () => {
    abrirGeracao();
    createTrack('Análise de Preços - Cancelou busca', { query: queryPesquisada });
    setFase('idle');
  };

  const refazerComFiltros = () => {
    const ultima = ultimaBuscaRef.current;
    if (!ultima || !resultadoRef.current || faseRef.current !== 'idle' || filaRef.current) return;
    createTrack('Análise de Preços - Mudou filtros', { query: ultima.query });
    setResultado(null);
    buscarPesado(ultima, filtrosAtuais());
  };

  const chaveFiltros = chaveDosFiltros(filtrosAtuais());

  useEffect(() => {
    if (!userId) return;
    const salvo = carregarLote(userId);
    loteRef.current = salvo;
    setLote(salvo);
  }, [userId]);

  useEffect(() => {
    if (!userId) return undefined;
    const aoMudarStorage = (e: StorageEvent) => {
      if (e.key !== null && e.key !== chaveDoLote(userId)) return;
      const salvo = carregarLote(userId);
      loteRef.current = salvo;
      setLote(salvo);
    };
    window.addEventListener('storage', aoMudarStorage);
    return () => window.removeEventListener('storage', aoMudarStorage);
  }, [userId]);

  useEffect(() => {
    if (periodoMeses !== PERIODO_PERSONALIZADO) gravarStorage('pi_periodo', String(periodoMeses));
  }, [periodoMeses]);

  useEffect(() => {
    gravarStorage('pi_ufs', JSON.stringify(ufs));
  }, [ufs]);

  useEffect(() => {
    if (filtrosAplicadosRef.current === chaveFiltros) return undefined;
    if (periodoMeses === PERIODO_PERSONALIZADO && !dataDe && !dataAte) return undefined;
    if (!resultadoRef.current || fase !== 'idle' || filaRef.current) {
      filtrosAplicadosRef.current = chaveFiltros;
      return undefined;
    }
    const timer = window.setTimeout(() => {
      filtrosAplicadosRef.current = chaveFiltros;
      refazerComFiltros();
    }, 800);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chaveFiltros]);

  useEffect(() => {
    if (!loteRodando) return undefined;
    const confirmarSaida = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', confirmarSaida);
    return () => window.removeEventListener('beforeunload', confirmarSaida);
  }, [loteRodando]);

  useEffect(() => {
    document.title = 'Análise de Mercado 3.0 - ConLicitação';
    priceIntelligenceApi
      .health()
      .then((h) => setTotalItens(h.points_count || null))
      .catch(() => setTotalItens(null));

    if (userId)
      manterSoResultadosDe(
        userId,
        (loteRef.current ? loteRef.current.itens : []).map((i) => i.id),
      );

    const params = new URLSearchParams(location.search);
    const q = (params.get('q') || '').trim();
    createTrack('Análise de Preços - Acessou', { origem: q ? 'refazer_busca' : 'direto' });
    if (q) {
      const periodo = Number(params.get('periodo'));
      const filtros: FiltrosPesquisa = {
        periodoMeses:
          periodo === PERIODO_PERSONALIZADO || PERIODOS_VALIDOS.indexOf(periodo) >= 0
            ? periodo
            : PERIODO_PADRAO,
        dataDe: params.get('de') || '',
        dataAte: params.get('ate') || '',
        ufs: ufsValidas((params.get('ufs') || '').split(',')),
      };
      history.replace(location.pathname);
      aplicarFiltros(filtros);
      iniciarBusca(q, filtros);
    }

    return () => {
      geracaoRef.current += 1;
      if (cancelRef.current) cancelRef.current.cancel();
      if (concluirTimerRef.current) window.clearTimeout(concluirTimerRef.current);
      if (filaRef.current) {
        filaRef.current.cancelado = true;
        if (filaRef.current.cancel) filaRef.current.cancel.cancel();
      }
      salvarTodasCanceladoRef.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const confirmarSalvar = async (nome: string, observacoes: string, batchName: string) => {
    if (!snapshotSalvar) return;
    const filtros = filtrosDaBuscaRef.current || filtrosAtuais();
    const personalizado = filtros.periodoMeses === PERIODO_PERSONALIZADO;
    const item =
      salvarItemId && loteRef.current
        ? loteRef.current.itens.find((i) => i.id === salvarItemId) || null
        : null;
    setSalvando(true);
    setErroSalvar(null);
    try {
      const salva = await priceIntelligenceApi.createSaved({
        name: nome,
        query: queryPesquisada.slice(0, 255),
        periodMonths: personalizado ? null : filtros.periodoMeses,
        dateFrom: personalizado ? filtros.dataDe || null : null,
        dateTo: personalizado ? filtros.dataAte || null : null,
        ufs: filtros.ufs.length ? filtros.ufs : null,
        notes: observacoes || null,
        batchName: batchName || null,
        snapshot: {
          ...snapshotSalvar,
          item: item
            ? { numero: item.numero, quantidade: item.quantidade, unidade: item.unidade }
            : null,
        },
      });
      if (item) mudarLote((atual) => (atual ? marcarSalvo(atual, item.id, salva.id) : atual));
      setSnapshotSalvar(null);
      setSalvaComSucesso(true);
      window.setTimeout(() => setSalvaComSucesso(false), 6000);
    } catch (err) {
      setErroSalvar(extrairErro(err));
    } finally {
      setSalvando(false);
    }
  };

  const abrirSalvar = (snapshot: SavedSearchSnapshot) => {
    setErroSalvar(null);
    setSalvarItemId(itemAtivoRef.current);
    setSnapshotSalvar(snapshot);
  };

  const pesquisarItem = (item: ItemLote) => {
    if (fase !== 'idle' && fase !== 'clarificando') return;
    if (filaRef.current) return;
    setItemAtivoId(item.id);
    itemAtivoRef.current = item.id;
    iniciarBusca(textoDeBusca(item));
  };

  const verResultadoDoItem = async (item: ItemLote) => {
    if (fase === 'analisando' || fase === 'buscando' || fase === 'concluindo') return;
    const { geracao } = abrirGeracao();
    const guardado = await obterResultado(item.id);
    if (geracao !== geracaoRef.current) return;
    if (!guardado) {
      if (!filaRef.current) pesquisarItem(item);
      return;
    }
    setItemAtivoId(item.id);
    itemAtivoRef.current = item.id;
    setQuery(guardado.busca.query);
    setQueryPesquisada(guardado.busca.query);
    aplicarFiltros(guardado.filtros);
    filtrosDaBuscaRef.current = guardado.filtros;
    ultimaBuscaRef.current = guardado.busca;
    setAnalise(guardado.analise);
    setDetalhes('');
    setProjetoQtd(null);
    setErro(null);
    setFase('idle');
    setResultado(guardado.resultado);
    setVersaoResultado((v) => v + 1);
    setTermoPanorama(guardado.busca.query);
    setFiltrosPanorama(FILTROS_AMOSTRAS_VAZIOS);
    createTrack('Análise de Preços - Abriu item do lote', {});
  };

  const pesquisarTodos = async () => {
    if (filaRef.current || fase === 'analisando' || fase === 'buscando') return;
    const fila = (loteRef.current ? loteRef.current.itens : [])
      .filter((i) => i.status === 'pendente')
      .map((i) => i.id);
    if (!fila.length) return;

    const controle: { cancelado: boolean; cancel: CancelTokenSource | null } = {
      cancelado: false,
      cancel: null,
    };
    filaRef.current = controle;
    const filtros = filtrosAtuais();
    const parametros = parametrosDosFiltros(filtros);
    setAviso(null);
    setFalhas({});
    createTrack('Análise de Preços - Pesquisou lote', { itens: fila.length });

    let feitos = 0;
    let comFalha = 0;
    let falhasSeguidas = 0;

    for (let i = 0; i < fila.length; i += 1) {
      const id = fila[i];
      if (controle.cancelado) break;
      const item = loteRef.current ? loteRef.current.itens.find((it) => it.id === id) : null;
      if (item && item.status === 'pendente') {
        setAndamento({ total: fila.length, feitos, falhas: comFalha, atualId: id });
        try {
          const texto = textoDeBusca(item);
          controle.cancel = criarCancelamento();
          let analiseDoItem: AnalisarResponse | null = null;
          try {
            // eslint-disable-next-line no-await-in-loop
            analiseDoItem = await priceIntelligenceApi.analyze(texto, controle.cancel);
          } catch (err) {
            if (controle.cancelado) throw err;
          }
          const contexto = contextoDaAnalise(texto, analiseDoItem);
          // eslint-disable-next-line no-await-in-loop
          const data = await priceIntelligenceApi.search(
            { ...contexto, ...parametros },
            controle.cancel,
          );
          if (controle.cancelado) break;
          registrarResultado(
            id,
            {
              resultado: data,
              busca: contexto,
              filtros,
              analise: analiseDoItem,
              quando: new Date().toISOString(),
            },
            analiseDoItem && analiseDoItem.clarificacao.necessaria
              ? analiseDoItem.clarificacao.motivo ||
                  'A IA pediria mais detalhes antes de pesquisar.'
              : null,
          );
          feitos += 1;
          falhasSeguidas = 0;
        } catch (err) {
          if (controle.cancelado) break;
          comFalha += 1;
          falhasSeguidas += 1;
          setFalhas((atual) => ({ ...atual, [id]: true }));
          if (falhasSeguidas >= 3) break;
        }
      }
    }

    if (filaRef.current === controle) filaRef.current = null;
    setAndamento(null);
    if (controle.cancelado) return;
    if (falhasSeguidas >= 3) {
      setAviso({
        tom: 'atencao',
        texto:
          'A fila parou depois de 3 falhas seguidas — a base pode estar sobrecarregada. Aguarde um minuto e pesquise os pendentes de novo.',
      });
    } else if (comFalha > 0) {
      setAviso({
        tom: 'atencao',
        texto: `${comFalha} ${
          comFalha === 1
            ? 'item falhou e continua pendente'
            : 'itens falharam e continuam pendentes'
        }: pesquise os pendentes para tentar de novo.`,
      });
    } else if (feitos > 0) {
      setAviso({
        tom: 'ok',
        texto: `${feitos} ${
          feitos === 1 ? 'item pesquisado' : 'itens pesquisados'
        }. Abra cada um para conferir as amostras ou salve todos de uma vez.`,
      });
    }
  };

  const pararLote = () => {
    const controle = filaRef.current;
    if (!controle) return;
    controle.cancelado = true;
    if (controle.cancel) controle.cancel.cancel();
    filaRef.current = null;
    setAndamento(null);
  };

  const descartarResultadosForaDe = (novo: Lote | null) => {
    const ficam: Record<string, boolean> = {};
    (novo ? novo.itens : []).forEach((i) => {
      ficam[i.id] = true;
    });
    Object.keys(resultadosRef.current).forEach((id) => {
      if (!ficam[id]) delete resultadosRef.current[id];
    });
    if (userId) manterSoResultadosDe(userId, Object.keys(ficam));
  };

  const importarItens = (
    arquivo: string,
    itens: RascunhoItem[],
    modo: 'substituir' | 'adicionar',
  ) => {
    const { lote: novo, descartados } = montarLote(loteRef.current, arquivo, itens, modo);
    mudarLote(() => novo);
    if (modo === 'substituir') {
      setItemAtivoId(null);
      setFalhas({});
      descartarResultadosForaDe(novo);
    }
    setImportarAberto(false);
    createTrack('Análise de Preços - Importou itens para o lote', { itens: itens.length, modo });
    setAviso(
      descartados > 0
        ? {
            tom: 'atencao',
            texto: `${descartados} ${
              descartados === 1 ? 'item ficou' : 'itens ficaram'
            } de fora: a lista comporta ${novo.itens.length} itens.`,
          }
        : null,
    );
  };

  const removerDoLote = (id: string) => {
    mudarLote((atual) => (atual ? removerItem(atual, id) : atual));
    if (itemAtivoRef.current === id) setItemAtivoId(null);
    descartarResultadosForaDe(loteRef.current);
  };

  const limparLote = () => {
    const mensagem =
      'Remover todos os itens do arquivo? As pesquisas já salvas continuam em Pesquisas salvas.';
    // eslint-disable-next-line no-alert
    if (!window.confirm(mensagem)) return;
    mudarLote(() => null);
    setItemAtivoId(null);
    setAviso(null);
    setFalhas({});
    descartarResultadosForaDe(null);
  };

  const exportarLote = () => {
    if (!loteRef.current) return;
    createTrack('Análise de Preços - Exportou lote', { itens: loteRef.current.itens.length });
    exportarLoteCsv(loteRef.current);
  };

  const itensPorSalvar = lote
    ? lote.itens.filter((i) => i.status === 'pesquisado' && i.resumo && i.resumo.valor !== null)
    : [];

  const salvarTodas = async () => {
    const atual = loteRef.current;
    if (!atual || salvandoTodas || !itensPorSalvar.length) return;
    const nomeLote = nomeDoLote(atual);
    const quantas = `${itensPorSalvar.length} ${
      itensPorSalvar.length === 1 ? 'pesquisa' : 'pesquisas'
    }`;
    const mensagem = `Salvar ${quantas} no lote “${nomeLote}”? Cada item vira uma pesquisa salva com todas as amostras.`;
    // eslint-disable-next-line no-alert
    if (!window.confirm(mensagem)) return;

    salvarTodasCanceladoRef.current = false;
    const alvo = itensPorSalvar.slice();
    let salvas = 0;
    let naoSalvas = 0;
    setSalvandoTodas({ feitos: 0, total: alvo.length });
    createTrack('Análise de Preços - Salvou lote', { itens: alvo.length });

    for (let i = 0; i < alvo.length; i += 1) {
      if (salvarTodasCanceladoRef.current) return;
      const item = alvo[i];
      // eslint-disable-next-line no-await-in-loop
      const guardado = await obterResultado(item.id);
      if (!guardado) {
        naoSalvas += 1;
      } else {
        const f = guardado.filtros;
        const personalizado = f.periodoMeses === PERIODO_PERSONALIZADO;
        try {
          // eslint-disable-next-line no-await-in-loop
          const salva = await priceIntelligenceApi.createSaved({
            name: nomeDaPesquisaDoItem(item),
            query: guardado.busca.query.slice(0, 255),
            periodMonths: personalizado ? null : f.periodoMeses,
            dateFrom: personalizado ? f.dataDe || null : null,
            dateTo: personalizado ? f.dataAte || null : null,
            ufs: f.ufs.length ? f.ufs : null,
            batchName: nomeLote,
            snapshot: {
              ...snapshotDoResultado(guardado.resultado),
              item: { numero: item.numero, quantidade: item.quantidade, unidade: item.unidade },
            },
          });
          mudarLote((l) => (l ? marcarSalvo(l, item.id, salva.id) : l));
          salvas += 1;
        } catch (err) {
          naoSalvas += 1;
        }
      }
      setSalvandoTodas({ feitos: salvas + naoSalvas, total: alvo.length });
    }

    setSalvandoTodas(null);
    setAviso(
      naoSalvas > 0
        ? {
            tom: 'atencao',
            texto: `${salvas} ${
              salvas === 1 ? 'pesquisa salva' : 'pesquisas salvas'
            } no lote “${nomeLote}”. ${naoSalvas} ${
              naoSalvas === 1 ? 'item não foi salvo' : 'itens não foram salvos'
            } — abra o resultado e salve pela tela, ou pesquise de novo.`,
          }
        : {
            tom: 'ok',
            texto: `${salvas} ${
              salvas === 1 ? 'pesquisa salva' : 'pesquisas salvas'
            } no lote “${nomeLote}”.`,
          },
    );
  };

  const desvincularItem = () => {
    setItemAtivoId(null);
    itemAtivoRef.current = null;
  };

  const buscando = fase === 'analisando' || fase === 'buscando' || fase === 'concluindo';
  const podeRefinar = Boolean(analise && analise.clarificacao.perguntas.length > 0);
  const posicaoAtivo = itemAtivo && lote ? lote.itens.indexOf(itemAtivo) + 1 : 0;

  // Fase alfa: acesso restrito a clientes alfa_testers e clientes liberados.
  if (!canAccessPriceIntelligence(profile)) return <Redirect to="/painel" />;
  const bloqueio = bloqueioDaAssinatura(profile);
  if (bloqueio.bloqueada)
    return (
      <div className="page-container">
        {bloqueio.representada ? (
          <BlockedSubscriptionRepresentant />
        ) : (
          <BlockedSubscription paralyzed={bloqueio.paralisada} />
        )}
      </div>
    );

  return (
    <div className="page-container">
      <div className="container-fluid big-resolutions">
        <Container>
          <PriceIntelligenceSearch
            query={query}
            onChangeQuery={setQuery}
            periodoMeses={periodoMeses}
            onChangePeriodo={setPeriodoMeses}
            ufs={ufs}
            onChangeUfs={setUfs}
            dataDe={dataDe}
            dataAte={dataAte}
            onChangeDatas={(de, ate) => {
              setDataDe(de);
              setDataAte(ate);
            }}
            onSearch={(texto) => iniciarBusca(texto)}
            onImportar={() => setImportarAberto(true)}
            buscando={buscando}
            loteRodando={loteRodando}
            totalItens={totalItens}
            mostrarSugestoes={!resultado && fase === 'idle' && !lote}
          />

          {erro && (
            <div className="alert alert-danger pi-error" role="alert">
              {erro}
            </div>
          )}

          {lote && (
            <PriceIntelligenceBatch
              lote={lote}
              itemAtivoId={itemAtivoId}
              ocupado={buscando}
              compacto={Boolean(resultado) || buscando || fase === 'clarificando'}
              andamento={andamento}
              salvando={salvandoTodas}
              falhas={falhas}
              porSalvar={itensPorSalvar.length}
              aviso={aviso}
              onFecharAviso={() => setAviso(null)}
              onPesquisar={pesquisarItem}
              onVerResultado={verResultadoDoItem}
              onRemover={removerDoLote}
              onAbrirSalva={(id) => window.open(`/pesquisa_de_precos/salvas/${id}`, '_blank')}
              onLimpar={limparLote}
              onImportar={() => setImportarAberto(true)}
              onPesquisarTodos={pesquisarTodos}
              onParar={pararLote}
              onExportar={exportarLote}
              onSalvarTodas={salvarTodas}
            />
          )}

          {itemAtivo && (
            <div className="active-item">
              <i className="material-icons">assignment</i>
              <span>
                <strong>
                  Item {itemAtivo.numero || posicaoAtivo} de {lote ? lote.itens.length : 0} do
                  arquivo
                </strong>
                {itemAtivo.quantidade !== null &&
                  ` · ${formatarQuantidade(itemAtivo.quantidade)} ${itemAtivo.unidade || ''}`}{' '}
                · o resultado e a pesquisa salva ficam neste item
              </span>
              <button type="button" onClick={desvincularItem} title="Desvincular do item">
                <i className="material-icons">close</i>
              </button>
            </div>
          )}

          {fase === 'clarificando' && analise && (
            <PriceIntelligenceClarification
              analise={analise}
              detalhes={detalhes}
              onChangeDetalhes={setDetalhes}
              onConfirmar={confirmarClarificacao}
              buscando={buscando || loteRodando}
            />
          )}

          {buscando && (
            <PriceIntelligenceProgress
              query={queryPesquisada}
              onCancel={cancelarBusca}
              totalItens={totalItens}
              concluida={fase === 'concluindo'}
            />
          )}

          {resultado && (fase === 'idle' || fase === 'clarificando') && (
            <PriceIntelligenceResults
              key={versaoResultado}
              resultado={resultado}
              onSalvar={abrirSalvar}
              periodoMeses={periodoMeses}
              onAmpliarPeriodo={() => setPeriodoMeses(12)}
              projetoQtd={projetoQtd}
              podeRefinar={podeRefinar && fase === 'idle'}
              onRefinar={() => setFase('clarificando')}
              regiaoDestaque={regiaoUnicaDe(ufs)}
              loteRodando={loteRodando}
              onFiltros={setFiltrosPanorama}
            />
          )}

          {!resultado && fase === 'idle' && !lote && (
            <div className="empty-state">
              <div className="empty-icon">
                <i className="material-icons">query_stats</i>
              </div>
              <h5>Pesquise um item para começar</h5>
              <p>
                A busca entende linguagem natural, encontra preços homologados nos diversos portais
                de compras integrados e a curadoria de IA mantém só os resultados realmente
                comparáveis — para você saber a que preço o mercado fecha antes de dar o seu lance.
              </p>
              <button
                type="button"
                className="btn btn-link"
                onClick={() => setImportarAberto(true)}
              >
                <i className="material-icons">upload_file</i>
                Vai cotar um edital inteiro? Envie a planilha, o PDF ou o Word com os itens
              </button>
            </div>
          )}

          <PriceIntelligenceMarket
            termo={termoPanorama}
            alternativas={resultado ? resultado.queries_utilizadas || [] : []}
            refino={filtrosPanorama}
            periodoMeses={periodoMeses}
            dataDe={dataDe}
            ufs={ufs}
          />

          {salvaComSucesso && (
            <div className="alert alert-success save-toast" role="alert">
              <i className="material-icons">check_circle</i>
              Pesquisa salva!{' '}
              <a href="/pesquisa_de_precos/salvas" className="alert-link">
                Ver pesquisas salvas
              </a>
            </div>
          )}
        </Container>
      </div>

      <PriceIntelligenceSaveModal
        show={Boolean(snapshotSalvar)}
        onClose={() => setSnapshotSalvar(null)}
        snapshot={snapshotSalvar}
        query={queryPesquisada}
        nomeSugerido={itemAtivo ? nomeDaPesquisaDoItem(itemAtivo) : null}
        loteSugerido={itemAtivo && lote ? nomeDoLote(lote) : null}
        saving={salvando}
        error={erroSalvar}
        onConfirm={confirmarSalvar}
      />

      {importarAberto && (
        <PriceIntelligenceImportModal
          itensNoLote={lote ? lote.itens.length : 0}
          onClose={() => setImportarAberto(false)}
          onImportar={importarItens}
        />
      )}
      <AmbassadorToolFeedback toolSlug={PRICE_INTELLIGENCE_AMBASSADOR_SLUG} />
    </div>
  );
};

export default PriceIntelligence;
